/**
 * Directives for module mwApp.common
 */

import { always } from 'app/helpers/fn'
import Cropper from 'cropperjs'
import { PROVIDERS_CONST } from '../../app/core/constants'

/**
 * ngReallyClick directive <br>
 * A generic confirmation for risky actions.
 *
 * @class ngReallyClick
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:ngReallyClick
 *
 * @example
 * <button type="button" class="danger"
 *         ng-really-header="Remove?"
 *         ng-really-message="Are you sure?"
 *         ng-really-click="takeAction()"
 * >Action</button>
 */
/* @ngInject */
export function ngReallyClick ($parse, alertSvc) {
  return {
    restrict: 'A',
    scope: {
      ngReallyClick: '&' // declare a function binding for directive
    },
    link: function (scope, element, attrs) {
      element.on('click', function ($event) {
        if (attrs.ngReallyBlockEvent) {
          $event.preventDefault()
          $event.stopPropagation()
        }

        var text = attrs.ngReallyMessage || 'Are you sure?'
        var header = attrs.ngReallyHeader
        var msg = {
          text: header ? text : null,
          header: header || text,
          confirmButton: attrs.ngReallyConfirmButton || 'Yes',
          discardButton: attrs.ngReallyDiscardButton || 'No',
          headerIconClass: attrs.ngReallyHeaderIconClass || 'fa fa-exclamation-triangle',
          showHeaderIconClass: attrs.ngReallyHeaderIconClass || attrs.ngReallyHeaderIconClass
        }

        const onlyOk = attrs.ngReallyOnlyOk || false
        const options = Object.fromEntries(Object.entries({ onlyOk }).filter(([key, value]) => value))

        alertSvc.confirm(msg, options)
          .then(
            () => {
              scope.ngReallyClick()
            }
          ).catch(() => {
            if (onlyOk && scope.ngReallyClick) {
              scope.ngReallyClick()
            }
          })
      })
    }
  }
}

/**
 * ngReallyChange directive <br>
 * A generic confirmation for risky actions.
 *
 * @class ngReallyChange
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:ngReallyChange
 *
 * @example
 * <input type="checkbox"
 *         ng-really-change="takeAction()" required
 *         ng-really-change-param="true || false" - if set to false, no confirmation will be. default true
 *         ng-really-header="Note!"
 *         ng-really-message="Are you sure?"
 *         ng-really-change-status={string}  condition when confirmation will be requested on switching. default 'every'
 *         {  'on' - when switching on
 *            'off' - when switching off
 *            'every' - every time
 *            anything else - never   }
 *         ng-really-header-change-on="Note!"
 *         ng-really-message-change-on="Are you sure?"
 *         ng-really-header-change-off="Note!"
 *         ng-really-message-change-off="Are you sure?"
 *         ng-really-change-event="click" element event
 * >
 */
/* @ngInject */
export function ngReallyChange (alertSvc) {
  return {
    restrict: 'A',
    scope: {
      ngReallyChange: '&', // declare a function binding for directive
      ngReallyChangeParam: '=?',
      ngModel: '=?'
    },
    link: function (scope, element, attrs) {
      const event = attrs.ngReallyChangeEvent || 'change'
      element.on(event, function ($event) {
        if (attrs.ngReallyBlockEvent) {
          $event.preventDefault()
          $event.stopPropagation()
        }

        const checked = scope.ngModel
        const param = scope.ngReallyChangeParam === undefined || scope.ngReallyChangeParam
        const status = attrs.ngReallyChangeStatus || 'every'
        const text = attrs.ngReallyMessage || 'Are you sure?'
        const header = attrs.ngReallyHeader || 'Note!!'
        const textForOn = attrs.ngReallyMessageChangeOn || text
        const headerForOn = attrs.ngReallyHeaderChangeOn || header
        const textForOff = attrs.ngReallyMessageChangeOff || text
        const headerForOff = attrs.ngReallyHeaderChangeOff || header
        const customClasses = attrs.ngReallyClasses || ''

        const msgForOn = {
          text: textForOn,
          header: headerForOn,
          customClasses,
          confirmButton: attrs.ngReallyConfirmButton || 'Yes',
          discardButton: attrs.ngReallyDiscardButton || 'No'
        }
        const msgForOff = {
          text: textForOff,
          header: headerForOff,
          customClasses,
          confirmButton: attrs.ngReallyConfirmButton || 'Yes',
          discardButton: attrs.ngReallyDiscardButton || 'No'
        }

        const sendNotification = (condition = false, msg) => {
          if (condition && param) {
            alertSvc.confirm(msg)
              .then(
                () => {
                  scope.ngReallyChange()
                }
              )
              .catch(() => {
                scope.ngModel = !checked
              })
          } else {
            scope.ngReallyChange()
          }
        }

        switch (status) {
          case 'off':
            sendNotification(!checked, msgForOff)
            break
          case 'on':
            sendNotification(checked, msgForOn)
            break
          case 'every':
            sendNotification(true, checked ? msgForOn : msgForOff)
            break
          default:
            sendNotification(false)
            break
        }
      })
    }
  }
}

/**
 * sidebarMenu directive <br>
 * Creates live sidebar menu
 *
 * @todo optimize this
 *
 * @class sidebarMenu
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:sidebarMenu
 */
/* @ngInject */
export function sidebarMenu ($state, $rootScope, authSvc, storageSvc, InappSettings) {
  return {
    restrict: 'A',
    link: function (scope, element) {
      const sysInfo = storageSvc.local.get('systemInfo')
      scope.infos = sysInfo && JSON.parse(sysInfo)
      scope.noraUI = VERSION + '#' + COMMITHASH
      var selectedCls = 'selected'
      var highlightedCls = 'highlightedSearchMenuItem'

      // all <li>
      var lis = element.find('li')

      // Check if multiorg is enabled
      scope.isMultiorgEnabled = function () {
        return InappSettings._current && InappSettings._current.multiorgEnabled === true
      }

      // Load inapp settings if not already loaded
      if (!InappSettings._current) {
        InappSettings.lazyLoad()
          .catch(() => {})
      }

      // expand/collapse all
      scope.isExpanded = false
      scope.expand = function (event) {
        event.preventDefault()

        scope.isExpanded = !scope.isExpanded

        // expand|collapse all parental <li>
        for (var j = lis.length; j--;) {
          var li = angular.element(lis[j])
          if (li.data('parental')) {
            toggleItem(li, scope.isExpanded)
          }
        }
      }

      scope.goToLink = null
      scope.$watch('goTo', function (val) {
        scope.goToLink = null
        if (val !== undefined) {
          dehighlightItems()
        }

        if (!val || !val.length) {
          return
        }

        var j, li, href, text

        // get all items for further search
        var items = Object.create(null)
        for (j = 0; j < lis.length; j++) {
          li = angular.element(lis[j])
          href = li.data('sref')
          text = li.text()
          if (href && href !== '#') {
            items[text] = li
          }
          if (text.includes('Dashboard')) {
            scope.findDashboard = true
            items[text] = li
          }
        }

        // regular expressions
        var xFull = new RegExp('(' + val.split('').join('.*?') + ')', 'i')
        var vals = val.split('').map(function (_l, i, a) { return a.slice(0, i).join('') + a.slice(i).join('.*?') }).reverse()
        var xs = vals.map(function (v) { return new RegExp('^\\s*' + v, 'i') })
        var xf = vals.map(function (v) { return new RegExp(v, 'i') })
        Array.prototype.push.apply(xs, xf)

        // find first best matching item and highlight it
        for (j = 0; j < xs.length; j++) {
          for (text in items) {
            if (xs[j].test(text)) {
              if (Object.prototype.hasOwnProperty.call(items, text)) {
                li = items[text]
              }
              if (li[0].classList.contains('ng-hide')) {
                return
              }
              scope.goToLink = li.data('sref')
              highlightItem(li, xFull)
              selectItem(li.data('parent'))

              return
            } else {
              scope.goToDashboard = false
            }
          }
        }
      })

      // copy from stateDirectives.js from ui-router sources
      function parseStateRef (ref, current) {
        var preparsed = ref.match(/^\s*({[^}]*})\s*$/)
        var parsed
        if (preparsed) {
          ref = current + '(' + preparsed[1] + ')'
        }
        parsed = ref.replace(/\n/g, ' ').match(/^([^(]+?)\s*(\((.*)\))?$/)
        if (!parsed || parsed.length !== 4) {
          throw new Error("Invalid state ref '" + ref + "'")
        }
        return {
          state: parsed[1], paramExpr: parsed[3] || null
        }
      }

      scope.loadImage = function (imgName, imgRes) {
        return require('../../img/' + window.ENV.APP_PROVIDER + '/' + window.ENV.APP_PROVIDER + '-' + imgName + ($rootScope.appSkin.sidebar.includes('ui-dark') ? '_dark' : '') + '.' + imgRes)
      }

      scope.showBottomSidebarBranding = function () {
        return !PROVIDERS_CONST[window.ENV.APP_PROVIDER]?.hideSidebarBottomBranding
      }

      scope.gogogo = function (e) {
        switch (e.which) {
          case 13:
            if (scope.goToLink) {
              var ref = parseStateRef(scope.goToLink, $state.current.name)
              $state.go(ref.state, scope.$eval(ref.paramExpr))
            } else if (scope.goToDashboard) {
              $state.go('app.dashboard')
            }
            /* fall through */
          case 27:
            var goBar = document.getElementById('_goBar')
            if (goBar) {
              goBar.blur()
            }
        }
      }
      scope.goclear = function () {
        scope.goTo = null
        dehighlightItems()
      }

      scope.showDashboard = function () {
        if (authSvc.hasEnabledFlag('FEATURE_SECURED_DASHBOARD_ENABLED')) {
          return authSvc.hasAccess('viewDashboard')
        }
        return true
      }

      scope.isAuthenticated = authSvc.isAuthenticated()

      scope.showReports = function () {
        return authSvc.hasEnabledFlag('FEATURE_SUBSCRIBER_REPORTS_ENABLED') || authSvc.hasEnabledFlag('FEATURE_SUBSCRIBER_ACTIVITY_REPORTS_ENABLED') || authSvc.hasEnabledFlag('FEATURE_CPS_REPORTS_ENABLED') || authSvc.hasEnabledFlag('FEATURE_STREAM_REPORTS_ENABLED') || authSvc.hasEnabledFlag('FEATURE_ACTIVATION_AUDIT_REPORTS_ENABLED')
      }

      scope.showStreamServices = function () {
        const canViewStreamSettings = authSvc.hasEnabledFlag('FEATURE_STREAM_SERVICES_ENABLED') &&
          authSvc.hasAccess('viewStreamService', scope)
        const canViewSetrixSections = authSvc.hasEnabledFlag('FEATURE_SETRIX_INTEGRATION_ENABLED') &&
          authSvc.hasAccess('viewSetrixSettings', scope)
        const canViewUrlPolicies = authSvc.hasAccess('viewUrlPolicy createUrlPolicy editUrlPolicy deleteUrlPolicy | | |', scope)

        return canViewStreamSettings || canViewSetrixSections || canViewUrlPolicies
      }

      scope.hasntFlag = function (flag) {
        if (authSvc.hasEnabledFlag(flag)) {
          lis = element.find('li')
          return false
        }
        return true
      }

      scope.loadSetplexImage = function () {
        return require('../../img/setplex.png')
      }

      // prepare all <li> items
      angular.forEach(lis, function (li, i) {
        li = angular.element(li)

        // bind index to each <li>
        li.data('i', i)

        // bind href to each <li>
        var href = li.find('a').attr('href')
        li.data('href', href)

        var sref = li.find('a').attr('ui-sref')
        li.data('sref', sref)

        // bind 'parental' flag
        if (href === '#' || href === '') { // this is parental menu item
          li.data('parental', true)

          // bind click event listener
          li.on('click', function () {
            if (!scope.isExpanded) {
              toggleItem(li)
            }
          })
        }
      })

      // find all parents for child <li>
      angular.forEach(lis, function (li, i) {
        li = angular.element(li)

        // find all children for parental <li>
        if (li.data('parental')) {
          angular.forEach(li.next().find('li'), function (child) {
            child = angular.element(child)
            var j = child.data('i')
            if (i !== j) {
              child.data('parent', i)
            }
          })
        }
      })

      // highlight item
      function highlightItem (li, regexp) {
        scope.goToDashboard = li.text().includes('Dashboard')

        var span = li ? li.find('span') : null
        if (span) {
          span.html(
            span.text().replace(regexp, '<font class="' + highlightedCls + '">$1</font>')
          )
        }
      }

      // dehighlight all items
      function dehighlightItems () {
        for (var j = lis.length; j--;) {
          var span = angular.element(lis[j]).find('span')
          if (span) {
            span.html(span.text())
          }
        }
      }

      // select item by index, or item itself
      function selectItem (i) {
        var j, li
        if (typeof i === 'object') {
          i.addClass(selectedCls)
        } else
        if (i !== undefined) {
          for (j = lis.length; j--;) {
            li = angular.element(lis[j])
            if (li.data('i') === i) {
              li.addClass(selectedCls)
              break
            }
          }
        }
      }

      // toggle item itself
      function toggleItem (i, on) {
        if (on === undefined) {
          i.toggleClass(selectedCls)
        } else {
          i[on ? 'addClass' : 'removeClass'](selectedCls)
        }
      }

      scope.getParamValue = function (param) {
        return window.ENV[param]
      }
    }
  }
}

/**
 * Allows to bind files to scope with "file-model" attribute <br>
 * {@link https://uncorkedstudios.com/blog/multipartformdata-file-upload-with-angularjs}
 *
 * @class fileModel
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:fileModel
 *
 * @example
 * <input type="file" accept="image/*" max-size="100000" ng-model="myModel" file-model>
 */
/* @ngInject */
export function fileModel () {
  return {
    restrict: 'A',
    require: 'ngModel',
    link: function (scope, element, attrs, ngModel) {
      // validator to check size
      var maxSize = attrs.maxSize ? parseInt(attrs.maxSize, 10) : 0
      if (maxSize) {
        ngModel.$validators.maxSize = function (modelValue, viewValue) {
          var value = modelValue || viewValue
          return !(value && value.size && value.size > maxSize)
        }
      }

      // validator to check type
      var accept = attrs.accept ? attrs.accept.split(',') : null
      if (accept && accept.length) {
        accept = accept.map(Function.prototype.call, String.prototype.trim)
        ngModel.$validators.accept = function (modelValue, viewValue) {
          var value = modelValue || viewValue
          return !(value && value.type && value.name &&
                   accept.indexOf(value.type) === -1 && // image/png,image/jpg
                   accept.indexOf(value.type.replace(/\/.+$/, '/*')) === -1 && // image/*
                   accept.indexOf('.' + value.name.split('.').pop()) === -1)
        }
      }

      // update scope
      element.on('change', function () {
        ngModel.$setViewValue(element[0].files[0], 'change')
      })
    }
  }
}

/**
 * Allows to compile ng-bind-html content <br>
 * {@link http://stackoverflow.com/questions/20297638/call-function-inside-sce-trustashtml-string-in-angular-js}
 *
 * @class compileTemplate
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:compileTemplate
 *
 * @example
 * <p ng-bind-html="myHTML" compile-template></p>
 */
/* @ngInject */
export function compileTemplate ($compile, $parse) {
  return {
    link: function (scope, element, attr) {
      var parsed = $parse(attr.ngBindHtml)

      function getStringValue () {
        return (parsed(scope) || '').toString()
      }

      // recompile if the template changes
      scope.$watch(getStringValue, function () {
        $compile(element, null, -9999)(scope) // the -9999 makes it skip directives so that we do not recompile ourselves
      })
    }
  }
}

/**
 * applyValidity directive
 * Allows to set multiple $setValidity flags from a single expression.
 *
 * Usage:
 * <input type="hidden"
 *        ng-model="model"
 *        apply-validity="{ someRule: expr1, anotherRule: expr2 }">
 *
 * Notes:
 * - Each key becomes a validity error name.
 * - Truthy value means "valid" for that key.
 */
/* @ngInject */
export function applyValidity ($parse) {
  return {
    restrict: 'A',
    require: 'ngModel',
    scope: false,
    link: function (scope, _element, attrs, ngModel) {
      const getter = $parse(attrs.applyValidity)

      const sync = () => {
        const map = getter(scope) || {}
        Object.keys(map).forEach((key) => {
          ngModel.$setValidity(key, !!map[key])
        })
      }

      scope.$watch(() => getter(scope), sync, true)
      sync()
    }
  }
}

/**
 * Dynamic compile template by url <br>
 * {@link https://stackoverflow.com/questions/21835471/angular-js-directive-dynamic-templateurl}
 *
 * @class dynamicTemplate
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:dynamicTemplate
 *
 * @example
 * <dynamic-template ng-repeat="template in templates" template-url="components/.../{{template}}.html"><dynamic-template>
 */
/* @ngInject */
export function dynamicTemplate () {
  return {
    restrict: 'E',
    link: function (scope, element, attrs) {
      scope.contentUrl = attrs.templateUrl
    },
    template: /* html */ '<div ng-include="contentUrl"></div>'
  }
}

/**
 * Get files by drag'n'drop or button and calls `on-file` handler with files (one by one)
 * {@link https://grobmeier.solutions/drag-drop-upload-angularjs-07092015.html}
 *
 * @class dndfile
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:dndfile
 *
 * @example
 * <dndfile accept="image/*" max-size="100000" on-file="uploadFn" args="[arg1, arg2]||undefined"/>
 */
/* @ngInject */
export function dndfile (alertLiteSvc) {
  // disable propogation and default actions for d'n'd events
  const preventDefaults = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  // hightlight drop area
  const highlight = (element) => () => {
    element.addClass('highlight')
  }

  // unhighlight drop area
  const unhighlight = (element) => () => {
    element.removeClass('highlight')
  }

  // handle drop event
  const handleDrop = (handleFile) => (e) => {
    const event = e.originalEvent || e
    handleFiles(event.dataTransfer && event.dataTransfer.files, handleFile)
  }

  // handle inner <input type='file' /> change event
  const handleChange = (handleFile) => function () { // have to be `function` because we use `this`
    handleFiles(this.files, handleFile)
  }

  // handle files, both from d'n'd and input field
  const handleFiles = (files, handleFile) => {
    if (files && files.length > 0) {
      // `files` is FileList object, not an array
      // @see https://developer.mozilla.org/en-US/docs/Web/API/FileList
      // so we use forEach from Array prototype to iterate over it
      Array.prototype.forEach.call(files, handleFile)
    }
  }

  // check size
  const checkSize = (maxSize) =>
    (!maxSize)
      ? always(true)
      : (size) => {
        if (size && size > maxSize) {
          alertLiteSvc.alert(`Selected file is bigger than ${maxSize} bytes`, 'danger')
          return false
        }
        return true
      }

  // check type
  const checkType = (accept) =>
    (!accept || !accept.length)
      ? always(true)
      : (type, name) => {
        accept = accept.map(Function.prototype.call, String.prototype.trim)
        if (
          accept.indexOf(type) === -1 && // image/png,image/jpg
          accept.indexOf(type.replace(/\/.+$/, '/*')) === -1 && // image/*
          accept.indexOf('.' + name.toLowerCase().split('.').pop()) === -1
        ) {
          alertLiteSvc.alert('Selected file has inappropriate type', 'danger')
          return false
        }
        return true
      }

  return {
    restrict: 'E',
    replace: true,
    scope: {
      onFile: '=',
      args: '=?', // need to be array
      currentForm: '=',
      withInfo: '=',
      vertical: '=',
      clearable: '=',
      clearImage: '='
    },
    template: /* html */ `
      <div>
        <i ng-if="clearable" class="icon-close delete-img" ng-click="clearImage()"></i>
        <div class="dndfile" ng-if="!vertical">
          <div class="dndText" ng-show="!withInfo">
            <span>Drag here to upload</span> <b>or</b> 
            <div class="inputBtn" ng-show="!disabled">
              <i class="fa fa-upload"></i> Click here to browse <input class="file" type="file" />
              <div class="dnd-info-text" ng-if="hasText" ng-bind="infoText"></div>
            </div>
          </div>
          <div class="uploading" ng-if="uploading"><i class="fa fa-spinner fa-pulse"></i></div>
          <div class="row" style="width: 100%; display: flex;" ng-show="withInfo">
            <div class="col-sm-2"></div>
            <div class="col-sm-7 nopadding">
              <div class="dndText">
                  <span>Drag here to upload</span> <b>or</b> 
                  <div class="inputBtn" ng-show="!disabled">
                    <i class="fa fa-upload"></i> Click here to browse <input class="file" type="file" />
                  </div>
                </div>
            </div>
            <div class="col-sm-3 nopadding" style="display: flex; flex-direction: column; justify-content: center;"  ng-show="!disabled && hasText">
              <div class="color-border_secondary-border-color" style="text-align: center; border: 1px dashed; border-radius: 5px; font-size: 12px; padding: 2px;">
                <div ng-if="hasText" ng-bind-html="infoText"></div>
              </div>
            </div>
          </div>
          <div class="uploading" ng-if="uploading"><i class="fa fa-spinner fa-pulse"></i></div>
        </div>
        <div class="dndfile" ng-if="vertical">
          <div style="height: 128px;"></div>
          <div class="dndText" ng-show="!withInfo">
            <span>Drag here to upload your file</span>
            <div class="inputBtn" ng-show="!disabled">
              <i class="fa fa-upload"></i> Browse file <input class="file" type="file" />
              <div class="dnd-info-text" ng-if="hasText" ng-bind="infoText"></div>
            </div>
          </div>
          <div class="uploading" ng-if="uploading"><i class="fa fa-spinner fa-pulse"></i></div>
          <div class="row" style="display: flex; flex-direction: column" ng-show="withInfo">
            <div class="nopadding">
              <div class="dndText">
                <b>Drag here to upload your file</b>
                <div class="inputBtn" ng-show="!disabled">
                  <i class="fa fa-upload"></i> Browse file <input class="file" type="file" />
                </div>
              </div>
            </div>
            <div class="nopadding" style="display: flex; flex-direction: column; justify-content: center;"  ng-show="!disabled && hasText">
              <div class="color-border_secondary-border-color" style="text-align: center; font-size: 12px; padding: 2px;">
                <div ng-if="hasText" ng-bind-html="infoText"></div>
              </div>
            </div>
          </div>
          <div class="uploading" ng-if="uploading"><i class="fa fa-spinner fa-pulse"></i></div>
        </div>
      </div>
    `,
    link: function (scope, element, attrs) {
      scope.infoText = attrs.infoText
      scope.hasText = !!attrs.infoText
      scope.disabled = !!attrs.disabled
      const sizeValid = checkSize(attrs.maxSize ? parseInt(attrs.maxSize, 10) : undefined)
      const typeValid = checkType(attrs.accept ? attrs.accept.split(',') : undefined)

      const callback = (file) => {
        if (scope.currentForm) {
          scope.currentForm.$setDirty()
        }
        scope.hasText = !!attrs.infoText
        scope.args = scope.args || []
        return sizeValid(file.size) && typeValid(file.type, file.name)
          ? (
            typeof attrs.promise !== 'undefined' && (attrs.promise === '' || /^(1|yes|true)$/i.test(attrs.promise))
              ? ((scope.uploading = true), scope.onFile(file, ...scope.args))
              : scope.onFile(file, ...scope.args)
          ) : false
      }
      setTimeout(() => {
        // disable propogation and default actions for d'n'd events
        ;['dragenter', 'dragover', 'dragleave', 'drop'].forEach((eventName) => {
          element.on(eventName, preventDefaults)
        })

        // hightlight drop area
        ;['dragenter', 'dragover'].forEach((eventName) => {
          element.on(eventName, highlight(element))
        })

        // unhighlight drop area
        ;['dragleave', 'drop'].forEach((eventName) => {
          element.on(eventName, unhighlight(element))
        })

        // handle drop event
        element.on('drop', handleDrop(callback))

        // alternative way -> handle input change
        element.find('input').on('change', handleChange(callback))
      }, 0)

      // reset input value
      scope.$on('dndfile.clear', function () {
        scope.uploading = false
        element.find('input').val('')
      })
    }
  }
}

/**
 * Loader with spinner for pages, blocks and elements
 *
 * @class loader
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:loader
 *
 * @example
 * <tag loader="<type-name>" data-event-loader="<event-name>">...
 * data-event-loader - optional
 */
/* @ngInject */
export function loader () {
  return {
    restrict: 'A',
    link: function (scope, element, attr) {
      const className = attr.loader
        ? 'loader-' + attr.loader
        : 'loader'

      if (scope.loading !== undefined && scope.loading !== null) {
        scope.$watch('loading', function (val) {
          if (val) {
            element.addClass(className)
          } else {
            element.removeClass(className)
          }
        })
      }

      if (attr.eventLoader !== undefined && attr.eventLoader !== null) {
        element.addClass(className)

        scope.$on(attr.eventLoader, function (event, data) {
          data && element.removeClass(className)
          !data && element.addClass(className)
        })
      }
    }
  }
}

/**
 * Check onload event by element
 *
 * @class onload
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:ngOnload
 *
 * @example
 * <tag ng-onload="eventHandler(event)" >...
 */
/* @ngInject */
export function ngOnload () {
  return {
    restrict: 'A',
    scope: {
      callback: '&ngOnload'
    },
    link: (scope, element) => {
      element.on('load', (event) => scope.callback({ event }))
    }
  }
}
export function onReadFile () {
  return {
    restrict: 'A',
    scope: {
      onReadFile: '&'
    },
    link: function (scope, element) {
      element.on('change', function (e) {
        // eslint-disable-next-line no-undef
        const reader = new FileReader()
        reader.onload = function (e) {
          scope.$apply(function () {
            scope.onReadFile({ $content: e.target.result })
          })
        }
        if ((e.srcElement || e.target).files[0]) {
          reader.readAsText((e.srcElement || e.target).files[0])
        }
      })
    }
  }
}
/**
 * Cropper functional for image
 *
 * @class cropper
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:cropper
 *
 * @example
 * <cropper crop-src="..." crop-options=>...
 */
/* @ngInject */
export function cropper ($timeout) {
  return {
    restrict: 'EA',
    replace: true,
    scope: {
      cropper: '=cropModel',
      cropSrc: '='
    },
    template: /* html */ `
      <img style="max-width: 100%;" ng-src="{{cropSrc}}" alt="" />
    `,
    link: (scope, element, attrs) => {
      const cropOptions = attrs.cropOptions ? attrs.cropOptions : {}

      element.bind('load', () => {
        $timeout(() => {
          scope.cropper = new Cropper(element[0], {
            ...JSON.parse(cropOptions)
          })
        })
      })

      scope.$on('cropper.destroy', function () {
        scope.cropper.destroy()
        scope.cropper = null
      })
    }
  }
}

/**
 * Directive for set wheel event on element.
 *
 * @class scroll
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:scroll
 *
 * @example
 * <div scroll="callbackFn"></div>
 */
/* @ngInject */
export function scroll () {
  return {
    restrict: 'A',
    scope: {
      scroll: '&'
    },
    link: function (scope, element) {
      element.bind('wheel', function (e) {
        if (typeof scope.scroll === 'function') {
          scope.scroll({ event: e })
        }
      })
    }
  }
}

/**
 * Directive format currency as HTML.
 *
 * @class cost
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:cost
 *
 * @example
 * <cost amount="value" currency="currency" symbol="currency.symbol" type=""amountType/>
 * symbol - optional
 * type - optional
 */
/* @ngInject */
export function cost () {
  return {
    restrict: 'E',
    scope: {
      amount: '=',
      currency: '=',
      symbol: '=?',
      type: '=?'
    },
    link: function (scope) {
      scope.symbol = scope.symbol || scope.currency?.symbol
      scope.amount = Math.round((+scope.amount + 0.00001) * 100) / 100 // round to at most 2 decimal places
      scope.isNaN = function (amount) {
        return isNaN(amount)
      }
    },
    template: /* html */ `
      <div ng-if="symbol && amount !== null && !isNaN(amount) && type!=='TAX_VAT'">
        <div ng-if="currency.after">{{amount}}{{currency.space ? ' ' : ''}}<bdi>{{symbol}}</bdi></div>
        <div ng-if="!currency.after"><bdi>{{symbol}}</bdi>{{currency.space ? ' ' : ''}}{{amount}}</div>
      </div>
      <div ng-if="type==='TAX_VAT'">
        {{amount}}%
      </div>
    `
  }
}

/**
 * Directive handles and trims input value according to step
 *
 * @class inputWithDecimals
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:inputWithDecimals
 *
 * @example
 * <input type="number" class="form-control" id="rentPrice" name="rentPrice" placeholder="Price"
 *  ng-model="vm.cs.pricing.rent.amount" min="0" step="0.01" max="99999.99" required input-with-decimals/>
 */

/* @ngInject */
export function inputWithDecimals () {
  return {
    restrict: 'A',
    require: 'ngModel',
    link: function (scope, element, attrs, ngModel) {
      element.on('input', function () {
        const value = +element[0].value
        if (value) {
          const getPower = (step) =>
            Math.trunc(step) === step ? 0 : 1 + getPower(step * 10)

          const trunc = Math.pow(10, getPower(attrs.step || 0.01))
          const roundedValue = Number(Math.round((value * trunc) + 'e' + 2) + 'e-' + 2)

          const newVal = Math.trunc(roundedValue) / trunc

          if (value === newVal) {
            return
          }
          ngModel.$setViewValue(newVal)
          ngModel.$render()
        }
      })
    }
  }
}

/* @ngInject */
export function highlightEdit () {
  return {
    require: 'ngModel',
    scope: false,
    link: function (scope, el, attrs, ngModelCtrl) {
      function saveCaretOffset (el) {
        const sel = window.getSelection()
        if (!sel || sel.rangeCount === 0) return null

        const range = sel.getRangeAt(0)
        const preRange = range.cloneRange()
        preRange.selectNodeContents(el[0])
        preRange.setEnd(range.endContainer, range.endOffset)
        return preRange.toString().length
      }

      function restoreCaretOffset (el, offset) {
        const range = document.createRange()
        const sel = window.getSelection()
        let currentOffset = 0

        function traverse (node) {
          if (node.nodeType === Node.TEXT_NODE) {
            const nextOffset = currentOffset + node.length
            if (offset <= nextOffset) {
              range.setStart(node, offset - currentOffset)
              range.collapse(true)
              sel.removeAllRanges()
              sel.addRange(range)
              throw 'done'
            }
            currentOffset = nextOffset
          } else {
            for (let i = 0; i < node.childNodes.length; i++) {
              traverse(node.childNodes[i])
            }
          }
        }

        try {
          traverse(el[0])
        } catch (e) {
          if (e !== 'done') throw e
        }
      }

      function render () {
        const caretOffset = saveCaretOffset(el)
        const text = ngModelCtrl.$viewValue || ''

        // Разбиваем текст на части, включая маркеры {{ и }}
        const parts = text.split(/(\{\{|\}\})/)
        let result = ''
        let open = false

        for (let i = 0; i < parts.length; i++) {
          const part = parts[i]

          // export function highlightClassSimple (arr, model) {
          //   if (!(arr?.length && model?.includes('{{') && model?.includes('}}'))) return
          //
          //   const re = /{+\s*([^{}]*?)\s*}+/
          //   const variable = model.match(re)?.[1]
          //   return variable && arr?.includes(variable)
          // }

          // highlight-class="{{(vm.highlightClass() !== undefined) && (vm.highlightClass() ? 'color-additional-14' : 'color-error')}}"
          //
          const highlightClass = 'color-additional-14'
          if (part === '{{') {
            if (!open) {
              result += `<span class="${highlightClass}">{{`
              open = true
            } else {
              // Вложенные {{ — игнорируем
              result += '{{'
            }
          } else if (part === '}}') {
            if (open) {
              result += '}}</span>'
              open = false
            } else {
              // Закрывающая }} без открывающей — экранируем
              result += '}}'
            }
          } else {
            result += open
              ? part
              : part.replace(/[&<>"']/g, m => ({
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#39;'
              })[m])
          }
        }

        el.html(result)
        if (caretOffset !== null) restoreCaretOffset(el, caretOffset)
      }

      el.on('input', () => {
        const text = el.text()
        ngModelCtrl.$setViewValue(text)
        render()
      })

      ngModelCtrl.$render = render
    }
  }
}

/* @ngInject */
export function trackCursorContext ($timeout) {
  return {
    restrict: 'A',
    scope: false,
    link: function (scope, element, attrs) {
      const trigger = attrs.trackCursorContext || '{{'

      function getTextBeforeCursor () {
        const sel = window.getSelection()

        if (!sel || sel.rangeCount === 0) return null
        const range = sel.getRangeAt(0)

        if (!range.collapsed) return null

        const pre = range.cloneRange()
        pre.selectNodeContents(element[0])
        pre.setEnd(range.endContainer, range.endOffset)
        return pre.toString()
      }

      function update () {
        const active = document.activeElement === element[0]
        if (!active) return

        const textBeforeCursor = getTextBeforeCursor()
        if (textBeforeCursor == null) {
          scope.cursorContext = null
          scope.$applyAsync()
          return
        }

        const idx = textBeforeCursor.lastIndexOf(trigger)
        if (idx === -1) {
          scope.cursorContext = null
          scope.$applyAsync()
          return
        }

        // строго от конца триггера до позиции курсора
        const value = textBeforeCursor.slice(idx + trigger.length)

        scope.cursorContext = value // напрямую в $scope.cursorContext
        scope.$applyAsync()

        // console.log('const scope.cursorContext = ', scope.cursorContext)
      }

      element.on('input', update)
      element.on('keyup', update)

      document.addEventListener('selectionchange', () => {
        if (document.activeElement === element[0]) {
          $timeout(update)
        }
      })

      $timeout(update)
    }
  }
}

/* @ngInject */
export function cursorContextExtractor ($timeout) {
  return {
    restrict: 'A',
    scope: {
      cursorContextExtractor: '='
    },
    link: function (scope, element) {
      function getCursorOffset () {
        const sel = window.getSelection()
        if (!sel.rangeCount) return -1
        const range = sel.getRangeAt(0)
        const preRange = range.cloneRange()
        preRange.selectNodeContents(element[0])
        preRange.setEnd(range.endContainer, range.endOffset)
        return preRange.toString().length
      }

      function extractContext (text, cursorPos) {
        const result = {
          text, cursorPos, value: '', startIndex: -1, invalid: true
        }

        const leftBracketPosition = text.lastIndexOf('{{', cursorPos)
        let rightBracketPosition = null

        if (leftBracketPosition < 0) return result
        const nextLeftBracketPos = text.indexOf('{', cursorPos)
        rightBracketPosition = text.indexOf('}', cursorPos)

        result.invalid = false
        result.startIndex = leftBracketPosition + 2

        if ((rightBracketPosition > -1) && (nextLeftBracketPos < 0 || rightBracketPosition < nextLeftBracketPos)) {
          return {
            ...result,
            value: text.slice(leftBracketPosition + 2, rightBracketPosition)
          }
        }

        return {
          ...result,
          text: text.slice(0, cursorPos) + '}}' + text.slice(cursorPos),
          value: text.slice(leftBracketPosition + 2, cursorPos)
        }
      }

      function updateContext () {
        if (document.activeElement !== element[0] || !scope.cursorContextExtractor) return

        const text = element[0].textContent
        const cursorPos = getCursorOffset()
        const result = extractContext(text, cursorPos)
        scope.cursorContextExtractor.cursorContext = result
      }

      element.on('input keyup click', updateContext)

      document.addEventListener('selectionchange', () => {
        if (document.activeElement === element[0]) {
          updateContext()
        }
      })

      $timeout(updateContext)

      scope.$on('$destroy', () => {
        element.off('input keyup click', updateContext)
      })
    }
  }
}

/**
 * Directive with displaying current count/max count symbols input.
 *
 * @class inputWithCounter
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:inputWithCounter
 *
 * @example
 * <input-with-counter maxLength="50" name="name" symbol="currency.symbol" type=""amountType/>
 * symbol - optional
 * type - optional
 */
/* @ngInject */
export function inputWithCounter () {
  return {
    restrict: 'E',
    scope: {
      autocomplete: '@?',
      counterLength: '=?',
      disabled: '=?',
      fieldRequired: '=?',
      focusIf: '=?',
      form: '=',
      handleBlur: '=?',
      handleChange: '=?',
      onChange: '&?',
      inputClass: '@?',
      maskValue: '=?',
      maskWhen: '=?',
      maxLength: '@?',
      minLength: '=?',
      name: '@',
      ngModelOptionsObj: '=?',
      notInarray: '=?',
      passwordvalidator: '=?',
      pattern: '@?',
      placeholder: '@?',
      type: '@?',
      value: '='
    },
    link: function (scope, element, attrs) {
      const resolvedMaskValue = () => (scope.maskValue && scope.maskValue.length ? scope.maskValue : '****************')

      const normalizeValueToString = (val) => {
        if (val === undefined || val === null) return val
        if (scope.type === 'number') return val
        if (typeof val === 'string') return val.trim() + ''
        return val + ''
      }

      scope.value = normalizeValueToString(scope.value)
      scope.displayValue = scope.value
      scope.isMasked = false
      scope.hasFocus = false
      scope.valueLength = 0
      scope.syncing = false

      const updateLength = (raw) => {
        const v = raw === undefined || raw === null ? '' : (raw + '')
        scope.valueLength = (scope.type !== 'number') ? v.trim().length : v.length
        if (angular.isDefined(scope.counterLength)) {
          scope.counterLength = scope.valueLength
        }
      }

      const applyMaskIfNeeded = () => {
        const shouldMask = !!scope.maskWhen &&
          !scope.hasFocus &&
          scope.valueLength === 0 &&
          (scope.value === undefined || scope.value === null || scope.value === '')
        if (!shouldMask) {
          scope.isMasked = false
          scope.syncing = true
          scope.displayValue = scope.value
          scope.syncing = false
          updateLength(scope.displayValue)
          return
        }

        scope.isMasked = true
        scope.syncing = true
        scope.displayValue = resolvedMaskValue()
        scope.syncing = false
        updateLength('') // do not show counter for fake mask
      }

      scope.changeValue = (event) => {
        if (scope.isMasked) return
        updateLength(event.target.value)
      }

      scope.triggerChange = () => {
        if (scope.onChange) {
          scope.onChange()
        }
        if (scope.handleChange) {
          scope.handleChange(scope.name)
        }
      }

      scope.onBlur = () => {
        scope.hasFocus = false
        const inputEl = element[0].querySelector('input')
        if (inputEl && scope.maskWhen && !scope.isMasked) {
          const raw = inputEl.value
          if (raw && (scope.value === '' || scope.value === undefined || scope.value === null)) {
            scope.syncing = true
            scope.displayValue = raw
            scope.value = normalizeValueToString(raw)
            scope.syncing = false
            updateLength(raw)
          }
        }
        if (scope.maskWhen) applyMaskIfNeeded()
        if (scope.handleBlur) {
          scope.handleBlur()
        }
      }

      scope.onFocus = () => {
        scope.hasFocus = true
        if (!scope.isMasked || !scope.maskWhen) return
        scope.isMasked = false
        scope.syncing = true
        scope.displayValue = ''
        scope.value = ''
        scope.syncing = false
        updateLength('')
      }

      scope.$watch('value', (newVal) => {
        if (scope.syncing) return
        scope.value = normalizeValueToString(newVal)
        applyMaskIfNeeded()
      })

      scope.$watch('displayValue', (newVal) => {
        if (scope.syncing) return
        if (scope.isMasked && newVal === resolvedMaskValue()) return
        scope.value = newVal
        updateLength(newVal)
      })

      scope.$watchGroup(['maskWhen', 'maskValue', 'type'], () => {
        scope.value = normalizeValueToString(scope.value)
        applyMaskIfNeeded()
      })

      scope.$watch('disabled', () => {
        if (scope.disabled) {
          scope.curPattern = ''
        } else {
          scope.curPattern = scope.pattern
        }
      })

      applyMaskIfNeeded()

      // MW-13355: native paste/input sync only when counterLength is bound (Playback Token Secret Key).
      const inputEl = element[0].querySelector('input')
      if (inputEl && angular.isDefined(attrs.counterLength)) {
        const onInputOrPaste = (event) => {
          if (scope.isMasked) return
          scope.$apply(() => {
            const raw = event.target.value
            scope.syncing = true
            scope.displayValue = raw
            scope.value = normalizeValueToString(raw)
            scope.syncing = false
            updateLength(raw)
          })
        }
        angular.element(inputEl).on('input paste', onInputOrPaste)
        scope.$on('$destroy', () => {
          angular.element(inputEl).off('input paste', onInputOrPaste)
        })
      }
    },
    template: /* html */ `
      <div class="input-container">
        <input type="{{((isMasked && !maskValue) ? 'password' : (type || 'text'))}}" class="custom-input {{inputClass}}" ng-keydown="changeValue($event)"
               ng-change="triggerChange()" ng-blur="onBlur()" ng-focus="onFocus()" ng-keyup="changeValue($event)" ng-disabled="disabled"
               name="{{name}}" id="{{name}}" placeholder="{{placeholder}}" ng-model="displayValue" 
               ng-maxlength="{{disabled ? '' : maxLength}}" ng-minlength="minLength" ng-required="fieldRequired" 
               ng-pattern="curPattern" focus-if="focusIf" autocomplete="new-password" ng-trim="{{trim}}" 
               not-in-array="notInarray" password-validator="{{passwordvalidator || false}}"
               ng-model-options="ngModelOptionsObj"/>
        <div class="length-counter" ng-if="valueLength > 0"> 
          <p style="margin-top: 10px" ng-class="{'custom-has-error': valueLength > maxLength && !disabled}" ng-bind="valueLength + '/' + (maxLength || '∞')"></p>
        </div>
      </div>
    `
  }
}

/**
 * Directive with displaying current count/max count symbols for textarea.
 *
 * @class textareaWithCounter
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:textareaWithCounter
 *
 * @example
 * <textarea-with-counter maxLength="50" name="name" symbol="currency.symbol" type=""amountType/>
 * symbol - optional
 * type - optional
 */
/* @ngInject */
export function textareaWithCounter () {
  return {
    restrict: 'E',
    scope: {
      autoResize: '=?',
      disabled: '=?',
      fieldRequired: '=?',
      form: '=',
      handleChange: '=?',
      index: '@?',
      inputClass: '@?',
      maxHeight: '@?',
      maxLength: '@?',
      minHeight: '@?',
      name: '@',
      ngModelOptionsObj: '=?',
      pattern: '@?',
      placeholder: '@?',
      rows: '@',
      type: '@',
      value: '='
    },
    link: function (scope, element) {
      scope.valueLength = scope.value ? scope.value.trim().length : 0
      scope.changeValue = (event) => {
        scope.valueLength = event.target.value.trim().length
      }
      scope.$watch('value', () => {
        if (scope.value || scope.value === '') {
          scope.valueLength = scope.value.trim().length
        }
      })
      scope.$watch('fieldRequired', () => {
        // TODO add/remove validator
      })
      scope.onChange = () => {
        if (scope.handleChange) {
          scope.handleChange(scope.name)
        }
      }

      if (scope.autoResize) {
        const textarea = element.find('textarea')[0]

        // eslint-disable-next-line no-inner-declarations
        function adjustHeight () {
          if (!textarea) return

          textarea.style.height = 'auto'

          const scrollHeight = textarea.scrollHeight
          const minHeight = parseInt(scope.minHeight) || 40
          const maxHeight = parseInt(scope.maxHeight) || 200

          let newHeight = Math.max(scrollHeight, minHeight)
          newHeight = Math.min(newHeight, maxHeight)

          textarea.style.height = newHeight + 'px'
        }

        element.on('input', adjustHeight)
        element.on('keyup', adjustHeight)

        scope.$watch('value', function () {
          setTimeout(adjustHeight, 0)
        })

        scope.$on('$destroy', function () {
          element.off('input', adjustHeight)
          element.off('keyup', adjustHeight)
        })
      }
    },
    template: /* html */ `
      <div class="input-container">
          <textarea type="{{type}}" style="overflow-y: scroll;" class="custom-input {{inputClass}} fz-16" ng-keydown="changeValue($event)" ng-keyup="changeValue($event)" 
              name="{{name}}" id="{{name+index}}" placeholder="{{placeholder}}" rows="{{rows}}" ng-maxlength="{{maxLength}}" ng-change="onChange()"
              ng-required="fieldRequired" ng-disabled="disabled" ng-model="value" ng-pattern="pattern"
              ng-model-options="ngModelOptionsObj"/></textarea>
          <div class="length-counter" ng-if="valueLength > 0 ">
              <p ng-class="{'custom-has-error': valueLength > maxLength && !disabled && maxLength}" ng-bind="valueLength + '/' + (maxLength || '∞')"></p>
          </div>
      </div>
    `
  }
}

/**
 * Directive with displaying current count/max count symbols for textarea.
 *
 * @class packageLinks
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:packageLinks
 *
 * @example
 * <package-links currentUrl="generalInformation"/>
 */
/* @ngInject */
export function packageLinks ($sce, authSvc, InappSettings) {
  return {
    restrict: 'E',
    scope: {
      isNew: '=',
      currentUrl: '@',
      publicUrl: '='
    },
    link: function (scope) {
      scope.hasFlag = function (flag) {
        return authSvc.hasEnabledFlag(flag)
      }
      scope.isMultiorgEnabled = function () {
        return InappSettings._current && InappSettings._current.multiorgEnabled === true
      }
      scope.loadIcon = function () {
        return $sce.trustAsHtml(`<svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M5.625 6.75C5.625 6.15234 6.11719 5.625 6.75 5.625C7.34766 5.625 7.875 6.15234 7.875 6.75C7.875 7.38281 7.34766 7.875 6.75 7.875C6.11719 7.875 5.625 7.38281 5.625 6.75ZM12.375 11.25C12.375 11.8828 11.8477 12.375 11.25 12.375C10.6172 12.375 10.125 11.8828 10.125 11.25C10.125 10.6523 10.6172 10.125 11.25 10.125C11.8477 10.125 12.375 10.6523 12.375 11.25ZM7.3125 11.8477C6.99609 12.1992 6.46875 12.1992 6.15234 11.8477C5.80078 11.5312 5.80078 11.0039 6.15234 10.6523L10.6523 6.15234C10.9688 5.83594 11.4961 5.83594 11.8125 6.15234C12.1641 6.50391 12.1641 7.03125 11.8125 7.34766L7.3125 11.8477ZM12.0938 1.54688C13.2539 1.40625 14.4492 1.75781 15.3633 2.63672C16.2422 3.55078 16.5938 4.74609 16.4531 5.90625C17.3672 6.64453 18 7.76953 18 9C18 10.2656 17.3672 11.3906 16.4531 12.1289C16.5938 13.2539 16.2422 14.4844 15.3633 15.3633C14.4492 16.2773 13.2539 16.6289 12.0938 16.4883C11.3555 17.4023 10.2305 18 9 18C7.73438 18 6.60938 17.4023 5.87109 16.4883C4.71094 16.6289 3.51562 16.2773 2.60156 15.3633C1.72266 14.4844 1.37109 13.2539 1.51172 12.1289C0.597656 11.3906 0 10.2656 0 9C0 7.76953 0.597656 6.64453 1.51172 5.90625C1.37109 4.74609 1.72266 3.55078 2.60156 2.63672C3.51562 1.75781 4.71094 1.40625 5.87109 1.54688C6.60938 0.632812 7.73438 0 9 0C10.2305 0 11.3555 0.632812 12.0938 1.54688ZM6.67969 3.51562L6.04688 3.30469C5.27344 3.05859 4.39453 3.23438 3.79688 3.83203C3.19922 4.42969 3.02344 5.30859 3.26953 6.04688L3.48047 6.71484L2.84766 7.06641C2.14453 7.41797 1.6875 8.15625 1.6875 9C1.6875 9.87891 2.14453 10.6172 2.84766 10.9688L3.48047 11.2852L3.26953 11.9531C3.02344 12.7266 3.19922 13.5703 3.79688 14.2031C4.39453 14.8008 5.27344 14.9766 6.04688 14.7305L6.67969 14.5195L7.03125 15.1523C7.38281 15.8555 8.12109 16.3125 9 16.3125C9.84375 16.3125 10.582 15.8555 10.9336 15.1523L11.2852 14.5195L11.918 14.7305C12.6914 14.9766 13.5703 14.8008 14.168 14.2031C14.7656 13.5703 14.9414 12.7266 14.6953 11.9531L14.4844 11.2852L15.1172 10.9688C15.8203 10.6172 16.3125 9.87891 16.3125 9C16.3125 8.15625 15.8203 7.41797 15.1172 7.06641L14.4844 6.71484L14.6953 6.04688C14.9414 5.30859 14.7656 4.42969 14.168 3.83203C13.5703 3.23438 12.6914 3.05859 11.918 3.30469L11.2852 3.51562L10.9336 2.88281C10.582 2.17969 9.84375 1.6875 9 1.6875C8.12109 1.6875 7.38281 2.17969 7.03125 2.88281L6.67969 3.51562Z"/>
                                       </svg>`)
      }

      // Load inapp settings if not already loaded
      if (!InappSettings._current) {
        InappSettings.lazyLoad()
          .catch(() => {})
      }
    },
    template: /* html */ `
        <div class="row subLinks">
            <a class="btn btn-{{currentUrl === '' ? 'info' : 'buttons_tabs_button_default'}}" ng-href="{{currentUrl === '' ? '' : publicUrl}}"><i class="fas fa-edit"></i> General</a>
            <a ng-if="!isNew" class="btn btn-{{currentUrl === 'contentSets' ? 'info' : 'buttons_tabs_button_default'}}" ng-href="{{currentUrl === 'contentSets' ? '' : publicUrl + '/contentSets'}}"><i class="fas fa-film"></i> Content Sets</a>
            <a ng-if="!isNew" access="viewSubscription" class="btn btn-{{currentUrl === 'monetization-plans' ? 'info' : 'buttons_tabs_button_default'}}" ng-href="{{currentUrl === 'monetization-plans' ? '' : (publicUrl + '/monetization-plans')}}"><i class="fas fa-toolbox"></i> Monetization Plans</a>
            <a ng-if="!isNew && hasFlag('FEATURE_PARTNERS_ENABLED')" access="" class="btn btn-{{currentUrl === 'partner-products' ? 'info' : 'buttons_tabs_button_default'}}" ng-href="{{currentUrl === 'partner-products' ? '' : (publicUrl + '/partner-products')}}"><i class="fa fa-suitcase"></i> Partner Products</a>
            <a style="display: inline-flex; gap: 3px; align-items: center;" access="viewDiscountCampaigns" ng-if="!isNew" class="btn btn-{{currentUrl === 'discount-campaigns' ? 'info' : 'buttons_tabs_button_default'}}" ng-href="{{currentUrl === 'discount-campaigns' ? '' : (publicUrl + '/discount-campaigns')}}"><div ng-bind-html="loadIcon()" style="height: 20px"></div> Discount Campaigns</a>
<!--            <a ng-if="hasFlags(['VOD_SET_ENABLED', 'TV_SHOW_SET_ENABLED', 'SEASON_SET_ENABLED']) && !isNew" class="btn btn-{{currentUrl === 'discounts' ? 'info' : 'buttons_tabs_button_default'}}" ng-href="{{currentUrl === 'discounts' ? '' : publicUrl + '/discounts'}}"><i class="fas fa-badge-percent"></i> Coupons</a>-->
<!--            <a ng-if="!isNew" class="btn btn-{{currentUrl === 'stbSettings' ? 'info' : 'buttons_tabs_button_default'}}" ng-href="{{currentUrl === 'stbSettings' ? '' : publicUrl + '/stbSettings'}}"><i class="far fa-cog"></i> STB Settings</a>-->
            <a ng-if="!isNew" access="viewAnnouncement"  class="btn btn-{{currentUrl === 'announcements' ? 'info' : 'buttons_tabs_button_default'}}" ng-href="{{currentUrl === 'announcements' ? '' : publicUrl + '/announcements'}}"><i class="fa fa-bullhorn"></i> Announcements and Reminders</a>
            <a ng-if="!isNew" access="viewBannerCarousel viewFeaturedCarousel | editNetwork &" class="btn btn-{{currentUrl === 'customization' ? 'info' : 'buttons_tabs_button_default'}}" ng-href="{{currentUrl === 'customization' ? '' : publicUrl + '/customization'}}"><i class="far fa-images"></i> Apps Customization</a>
            <a ng-if="!isNew" access="viewNetworkPayment" class="btn btn-{{currentUrl === 'paymentServices' ? 'info' : 'buttons_tabs_button_default'}}" ng-href="{{currentUrl === 'paymentServices' ? '' : publicUrl + '/paymentServices'}}"><i class="fa fa-money"></i> Payment Services</a>
            <a ng-if="!isNew && hasFlag('FEATURE_SHOPPING_CART_MANAGEMENT_ENABLED')" access="editNetwork viewShoppingCart &" class="btn btn-{{currentUrl === 'shoppingCart' ? 'info' : 'buttons_tabs_button_default'}}" ng-href="{{currentUrl === 'shoppingCart' ? '' : publicUrl + '/shoppingCart'}}"><i class="fa fa-shopping-cart" aria-hidden="true"></i> Shopping Cart</a>
            <a ng-if="!isNew && hasFlag('FEATURE_APP_LOCALIZATION_ENABLED') && isMultiorgEnabled()" access="viewLocalization" class="btn btn-{{currentUrl === 'appLocalization' ? 'info' : 'buttons_tabs_button_default'}}" ng-href="{{currentUrl === 'appLocalization' ? '' : publicUrl + '/appLocalization'}}"><i class="far fa-tablet-rugged"></i> App Localization</a>
        </div>
    `
  }
}

/**
 * Custom input for type number.
 *
 * @class inputNumber
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:inputNumber
 *
 * @example
 * <input-number maxLength="50" name="name" symbol="currency.symbol" type=""amountType/>
 * symbol - optional
 * type - optional
 */
/* @ngInject */
export function inputNumber () {
  return {
    restrict: 'E',
    scope: {
      inputClass: '@?',
      fieldRequired: '@?',
      disabled: '=?',
      value: '=',
      name: '@',
      form: '=',
      placeholder: '@?',
      step: '@?',
      max: '@?',
      min: '=?',
      minValue: '=?'
    },
    link: function (scope) {
      const step = +scope.step || 1
      scope.increment = function () {
        if (!scope.value) {
          scope.value = 0
        }
        if (!scope.disabled) {
          scope.form[scope.name].$touched = true
          scope.value = scope.value + step
        }
      }
      scope.decrement = function () {
        if (!scope.value) {
          scope.value = 0
        }
        if (!scope.disabled) {
          scope.form[scope.name].$touched = true
          scope.value = scope.value - step
          if ((scope.minValue !== undefined) && (scope.value < scope.minValue)) {
            scope.value = scope.minValue
          }
        }
      }
    },
    template: /* html */ `
      <div class="input-number-container">
        <i class="fas fa-angle-up" ng-click="increment()"></i>
        <input type="number" class="custom-input-number {{inputClass}}" 
            name="{{name}}" id="{{name}}" placeholder="{{placeholder}}" 
            ng-required="{{fieldRequired}}" ng-disabled="disabled" ng-model="value"
            max="{{max}}" min="{{min}}"
            >
       <i class="fas fa-angle-down" ng-click="decrement()"></i>
      </div>
    `
  }
}

/**
 * Custom input for text tags.
 *
 * @class inputTags
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common:inputTags
 *
 * @example
 * <input-tags
 *              ng-disabled="true"
 *              ng-model="model"
 *              ng-required="true"
 *              form="form"
 *              name="name"
 *              field-name="Field name" // for example {{fieldName || 'Field'}} is required
 *              placeholder="placeholder"
 *              custom-validators="customNetworkValidators" // Array of or object custom validator
 * ></input-tags>
 */
/* @ngInject */
export function inputTags () {
  return {
    restrict: 'E',
    scope: {
      form: '=?',
      ngModel: '=?',
      ngDisabled: '=?',
      ngRequired: '=?',
      placeholder: '@?',
      name: '@?',
      fieldName: '@?',
      customValidators: '=?',
      ngClass: '@?',
      id: '@?',
      multiInput: '=?',
      multi: '=?'
    },
    link: function (scope) {
      scope._customValidatorNames = []

      scope.$watch('customValidators', function (newVal) {
        if (!scope.form || !scope.name || !scope.form[scope.name]) return

        const control = scope.form[scope.name]

        if (scope._customValidatorNames && scope._customValidatorNames.length) {
          scope._customValidatorNames.forEach((name) => {
            if (control.$validators[name]) {
              delete control.$validators[name]
            }
            // clear any stale validation errors for removed validators
            if (control.$error && Object.prototype.hasOwnProperty.call(control.$error, name)) {
              control.$setValidity(name, true)
            }
          })
        }

        if (!newVal) {
          scope._customValidatorNames = []
          return
        }

        const validatorsArray = Array.isArray(newVal) ? newVal : [newVal]
        const newNames = validatorsArray.map(v => v.name)
        scope._customValidatorNames = newNames

        validatorsArray.forEach((validator) => {
          control.$validators[validator.name] = validator.fn
        })

        control.$validate()
      }, true)

      if (!scope.ngModel) {
        scope.ngModel = []
      }

      if (!scope.multi) {
        scope.model = scope.ngModel[0]
      }

      scope.onBlur = function () {
        if (scope.multi) {
          const input = scope.model
          if (!input) return
          if (scope.multiInput) {
            const tagArray = input.split(' ')
            tagArray.forEach(tag => {
              if (!scope.ngModel.includes(tag) && !!tag.trim()) {
                scope.ngModel.push(tag.trim())
              }
            })
          } else {
            if (scope.ngModel.includes(input)) return

            scope.ngModel.push(input)
          }
          scope.model = null
        } else {
          scope.ngModel = [scope.model]
        }
      }
      scope.deleteItem = function (index) {
        scope.ngModel.splice(index, 1)
      }
      scope.onKeyPress = function (event) {
        if (event.keyCode === 13) {
          scope.onBlur()
        }
      }
    },
    template: /* html */ `
      <div class="{{ngClass}}" >
        <div ng-class="{'has-error': (form[name].$touched || form.$submitted) && form[name].$invalid}">
          <input ng-model="model" name="{{name}}" form="form" id="{{id || name}}"
            ng-disabled="ngDisabled" 
            ng-required="ngRequired && !ngModel.length"
            ng-blur="onBlur()"
            ng-keypress="onKeyPress($event)"
            class="form-control"
            placeholder="{{placeholder || 'Enter value'}}" type="text">
        </div>    
        <div class="text-danger" ng-messages="form[name].$error"
             ng-show="form[name].$touched || form.$submitted">
          <p class="aligned" ng-message="required">{{fieldName || 'Field'}} is required</p>
          <p ng-repeat="validator in customValidators" class="aligned" ng-message="{{validator.name}}">{{validator.errorText}}</p>
        </div>
        <div ng-if="multi">
          <div ng-repeat="item in ngModel" class="mt-10" style="display: inline-block">
              <span  class="label label-info right10">
                {{item}}
                <i ng-if="!ngDisabled" ng-click="deleteItem($index); form.$setDirty()" class="fal fa-times" style="cursor: pointer;"></i>
              </span>
            </div>
          </div>      
      </div>
    `
  }
}
/**
 * Displaying error text
 *
 * @class imageError
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:imageError
 *
 * @example
 * <img ng-src="url" image-error image-error-class="className" image-error-text="error text" />
 */
/* @ngInject */
export function imageError () {
  return {
    restrict: 'A',
    scope: {
      imageErrorClass: '@',
      imageErrorText: '@'
    },
    link: function (scope, _elem, _attr) {
      _elem.bind('error', function () {
        _elem[0].style.display = 'none'
        const el = document.createElement('div')
        el.classList.add(scope.imageErrorClass)
        el.innerText = scope.imageErrorText
        _elem[0].parentNode.append(el)
      })
    }
  }
}

/**
 * Displaying error text
 *
 * @class destroyHandler
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:destroyHandler
 *
 * @example
 * <div ng-if="CONDITION" destroy-handler on-destroy-object="vm.report" on-destroy-field="firstActivation" on-destroy="vm.onDestroyCondition()">
 */
/* @ngInject */
export function destroyHandler () {
  return {
    restrict: 'A',
    scope: {
      onDestroy: '&?',
      onDestroyObject: '=',
      onDestroyValue: '@?',
      onDestroyField: '@'
    },
    link: function (scope) {
      scope.$on('$destroy', function () {
        if (scope.onDestroyObject) {
          scope.onDestroyObject[scope.onDestroyField] = scope.onDestroyValue || null
        }
        if (scope.onDestroy) {
          scope.onDestroy() // Вызовите onDestroy, если она определена
        }
      })
    }
  }
}

/**
 * uiSelectOnOpen directive <br>
 * Executes a function when ui-select dropdown is opened.
 *
 * @class uiSelectOnOpen
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc directive
 * @name mwApp/common.directive:uiSelectOnOpen
 *
 * @example
 * <ui-select ng-model="selected" ui-select-on-open="loadData()">
 *   <ui-select-match placeholder="Select..."></ui-select-match>
 *   <ui-select-choices repeat="item in items">
 *     <span ng-bind="item.name"></span>
 *   </ui-select-choices>
 * </ui-select>
 */
/* @ngInject */
export function uiSelectOnOpen ($parse) {
  return {
    restrict: 'A',
    require: 'uiSelect',
    link: function (scope, element, attrs) {
      const onOpenExpression = attrs.uiSelectOnOpen

      if (!onOpenExpression) {
        return
      }

      const onOpenFn = $parse(onOpenExpression)

      // Watch for ui-select open state
      scope.$watch('$select.open', function (isOpen) {
        if (isOpen) {
          // Execute the function when dropdown opens
          onOpenFn(scope)
        }
      })
    }
  }
}
